import { useContext, useEffect, useMemo, useState } from "react";
import { MdArrowOutward, MdPhotoLibrary, MdPlace, MdVerified } from "react-icons/md";
import { AppContext } from "../App";
import { CDN_URL, getInlineLoader } from "../Helpers";

function stripHtml(value = "") {
  return String(value).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function parseCategories(value) {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch (error) {
    return [];
  }
}

function shortenText(value = "", limit = 180) {
  const cleanValue = stripHtml(value);

  if (cleanValue.length <= limit) {
    return cleanValue;
  }

  return `${cleanValue.slice(0, limit).trim()}...`;
}

export default function GalleryContent(props) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");

  async function init() {
    setReady(false);

    if (!appContext.allGallery) {
      await appContext.getAllGallery?.();
    }

    setReady(true);
  }

  useEffect(() => {
    init();
  }, []);

  const galleryItems = useMemo(() => {
    return (appContext.allGallery || []).map((item, index) => {
      const categories = parseCategories(item.categories);

      return {
        ...item,
        index,
        categories,
        descriptionText: stripHtml(item.description),
        previewText: shortenText(item.description),
        photoUrl: item.photo ? `${CDN_URL}${item.photo}` : "",
      };
    });
  }, [appContext.allGallery]);

  const categoryOptions = useMemo(() => {
    const allCategories = [];

    galleryItems.forEach((item) => {
      item.categories.forEach((category) => {
        if (!allCategories.includes(category)) {
          allCategories.push(category);
        }
      });
    });

    return allCategories;
  }, [galleryItems]);

  const filteredItems = useMemo(() => {
    if (activeCategory === "all") {
      return galleryItems;
    }

    return galleryItems.filter((item) => item.categories.includes(activeCategory));
  }, [activeCategory, galleryItems]);

  const featuredItem = filteredItems[0] || galleryItems[0] || null;
  const categoryCount = categoryOptions.length;
  const landscapeCount = galleryItems.filter((item) => item.photoUrl).length;

  if (!ready) {
    return (
      <div className="container mSupportLoading">
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <div className="MainBodySection gallery-page">
      <div className="gallery-hero-shell">
        <div className="container">
          <div className="row g-4 align-items-center">
            <div className="col-lg-6">
              <div className="tff-section-kicker">Gallery</div>
              <h1 className="gallery-page-title">
                A visual journey through Zanzibar moments, coastlines, culture, and safari memories.
              </h1>
              <p className="font-light tff-section-lead gallery-page-lead">
                Explore highlights from the experiences we help create, from island escapes and marine adventures to
                mainland wildlife scenes and guest travel moments across Tanzania.
              </p>

              <div className="gallery-stat-grid">
                <div className="gallery-stat-card">
                  <MdPhotoLibrary size={22} className="text-secondary mb-3" />
                  <h3 className="font-bold text-primary">{galleryItems.length}</h3>
                  <p className="mNoMargin font-light">Published gallery moments</p>
                </div>
                <div className="gallery-stat-card">
                  <MdVerified size={22} className="text-secondary mb-3" />
                  <h3 className="font-bold text-primary">{categoryCount}</h3>
                  <p className="mNoMargin font-light">Curated experience tags</p>
                </div>
                <div className="gallery-stat-card">
                  <MdPlace size={22} className="text-secondary mb-3" />
                  <h3 className="font-bold text-primary">{landscapeCount}</h3>
                  <p className="mNoMargin font-light">Scenes ready to explore</p>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              {featuredItem ? (
                <div className="gallery-feature-card">
                  <div
                    className="gallery-feature-image"
                    style={{
                      backgroundImage: `linear-gradient(180deg, rgba(9, 20, 26, 0.05), rgba(9, 20, 26, 0.78)), url(${featuredItem.photoUrl})`,
                    }}
                  >
                    <div className="gallery-feature-copy">
                      <div className="gallery-feature-tags">
                        {(featuredItem.categories.length > 0 ? featuredItem.categories : ["Featured Moment"]).slice(0, 3).map((category) => (
                          <span key={category} className="gallery-tag-light">
                            {category}
                          </span>
                        ))}
                      </div>
                      <h2 className="font-light text-light mt-3 mb-3">Featured travel moment</h2>
                      <p className="font-light text-light mNoMargin">{featuredItem.previewText}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="card gallery-empty-card">
                  <div className="card-body p-4 p-lg-5 text-center">
                    <div className="tff-section-kicker">Gallery</div>
                    <h3 className="font-light text-primary mt-3">Fresh moments will appear here soon</h3>
                    <p className="font-light tff-section-lead mt-3 mb-0">
                      Published gallery items from the admin panel will automatically populate this page.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="gallery-filter-row">
          <div>
            <div className="tff-section-kicker">Browse Collection</div>
            <h2 className="tff-section-title mb-0">
              Stories framed in images.
              <span className="tff-section-underline"></span>
            </h2>
          </div>

          <div className="gallery-filter-pills">
            <button
              className="gallery-filter-pill"
              data-active={activeCategory === "all"}
              onClick={() => setActiveCategory("all")}
            >
              All
            </button>

            {categoryOptions.map((category) => (
              <button
                key={category}
                className="gallery-filter-pill"
                data-active={activeCategory === category}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {filteredItems.length > 0 ? (
          <div className="row g-4">
            {filteredItems.map((item, index) => (
              <div key={item.uuid || index} className={`col-md-6 ${index % 3 === 0 ? "col-lg-7" : "col-lg-5"}`}>
                <div className="gallery-grid-card">
                  <div
                    className="gallery-grid-image"
                    style={{
                      backgroundImage: `linear-gradient(180deg, rgba(9, 20, 26, 0.04), rgba(9, 20, 26, 0.86)), url(${item.photoUrl})`,
                    }}
                  >
                    <div className="gallery-grid-overlay">
                      <div className="gallery-grid-tags">
                        {(item.categories.length > 0 ? item.categories : ["Zanzibar Experience"]).slice(0, 3).map((category) => (
                          <span key={category} className="gallery-tag-light">
                            {category}
                          </span>
                        ))}
                      </div>

                      <p className="font-light text-light gallery-grid-description">{item.previewText}</p>

                      <button
                        className="btn btn-secondary btn-rounded gallery-grid-cta"
                        onClick={() => {
                          appContext.navTo({ item: "contact_us" });
                        }}
                      >
                        Plan a Similar Experience
                        <MdArrowOutward size={18} className="ms-2" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card gallery-empty-card mt-4">
            <div className="card-body p-4 p-lg-5 text-center">
              <div className="tff-section-kicker">No Matches</div>
              <h3 className="font-light text-primary mt-3">There are no gallery items in this category yet</h3>
              <p className="font-light tff-section-lead mt-3 mb-0">
                Try another filter or publish more gallery images from the admin panel.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
